import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, RotateCcw } from "lucide-react";

/* Per-person access. Their role sets the baseline; every switch here is either an extra permission
   granted to this one individual or one taken away from them. */
export const MemberAccessDialog = ({ member, onClose, onSaved }) => {
  const [catalogue, setCatalogue] = useState(null);
  const [granted, setGranted] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!member) return;
    setGranted(new Set(member.permissions || []));
    api.get("/orgs/permissions").then(({ data }) => setCatalogue(data));
  }, [member]);

  if (!member) return null;

  const base = new Set(member.role_permissions || []);
  const toggle = (action) => setGranted((prev) => {
    const next = new Set(prev);
    if (next.has(action)) next.delete(action);
    else next.add(action);
    return next;
  });

  const save = async () => {
    setBusy(true);
    try {
      const all = (catalogue?.groups || []).flatMap((g) => g.actions.map((a) => a.value));
      const { data } = await api.patch(`/users/${member._id}/permissions`, {
        allow: all.filter((a) => granted.has(a) && !base.has(a)),
        deny: all.filter((a) => !granted.has(a) && base.has(a)),
      });
      toast.success(`Access updated for ${data.name}`);
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update access");
    }
    setBusy(false);
  };

  const changes = (catalogue?.groups || []).flatMap((g) => g.actions)
    .filter((a) => granted.has(a.value) !== base.has(a.value)).length;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="member-access-dialog"
                     className="max-h-[88vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display">{member.name}'s access</DialogTitle>
          <DialogDescription>
            {member.role_label} by role. Turn anything on or off for this person only —
            it takes effect on their next request.
          </DialogDescription>
        </DialogHeader>

        {!catalogue ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            <Loader2 className="mx-auto h-5 w-5 animate-spin" />
          </div>
        ) : (
          <div className="space-y-5">
            {catalogue.groups.map((group) => (
              <section key={group.group} data-testid={`access-group-${group.group}`}>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-royal/70">
                  {group.group}
                </p>
                <div className="mt-2 divide-y divide-border rounded-lg border border-border">
                  {group.actions.map((a) => {
                    const on = granted.has(a.value);
                    const changed = on !== base.has(a.value);
                    return (
                      <label key={a.value}
                             className="flex items-center justify-between gap-3 px-3 py-2.5">
                        <span className="text-sm text-royal-deep">
                          {a.label}
                          {changed && (
                            <span className="ml-2 text-[10px] uppercase tracking-wider text-gold">
                              {on ? "added" : "removed"}
                            </span>
                          )}
                        </span>
                        <Switch checked={on} onCheckedChange={() => toggle(a.value)}
                                data-testid={`access-toggle-${a.value}`} />
                      </label>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button type="button" variant="ghost" data-testid="access-reset-btn"
                  onClick={() => setGranted(new Set(base))}
                  className="gap-2 text-xs text-muted-foreground">
            <RotateCcw className="h-3.5 w-3.5" /> Reset (then Save)
          </Button>
          <Button type="button" onClick={save} disabled={busy || !catalogue}
                  data-testid="access-save-btn" className="min-h-[44px] bg-royal hover:bg-royal-deep">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                  : `Save${changes ? ` (${changes})` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
